import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import StudyModeCodeEditor from '../StudyMode/StudyModeCodeEditor';
import StudyNavbar from '../StudyMode/Shell/StudyNavbar';
import IconRenderer from '../ui/IconRenderer';
import useUserLanguages from '../../hooks/useUserLanguages';
import { formatLanguageDisplay } from '../../utils/roadmapDisplay';
import { getLangColors } from '../../utils/languageColors';
import PlaygroundSnippetIndex from './PlaygroundSnippetIndex';
import PlaygroundSaveSnippetDialog from './PlaygroundSaveSnippetDialog';
import {
  buildPlaygroundLanguageOptions,
  normalizePlaygroundLanguage,
  playgroundCodeStorageKey,
  PLAYGROUND_LANGUAGE_STORAGE_SUFFIX,
} from './playgroundLanguages';
import {
  createPlaygroundSnippet,
  findPlaygroundSnippet,
  getSnippetCodePreview,
  normalizeSnippetTitle,
  PLAYGROUND_UNTITLED_SNIPPET_TITLE,
  readActiveSnippetId,
  readPlaygroundSnippets,
  suggestSnippetTitle,
  updatePlaygroundSnippet,
  writeActiveSnippetId,
  writePlaygroundSnippets,
} from './playgroundSnippets';
import {
  deletePlaygroundSnippetRemote,
  listPlaygroundSnippets,
  mergePlaygroundSnippets,
  snippetsNeedingRemoteUpsert,
  upsertPlaygroundSnippet,
  upsertPlaygroundSnippetsBatch,
} from '../../services/playgroundSnippetsService';
import '../StudyMode/Shell/StudyShell.css';
import '../StudyMode/StudyRoadmapContextPanel.css';
import '../StudyMode/Study.css';
import './PlaygroundStudyMode.css';

const PLAYGROUND_REMOTE_UPSERT_DEBOUNCE_MS = 1500;

function PlaygroundLanguageDropdown({ value, options, onSelect, disabled }) {
  const containerRef = useRef(null);
  const listId = React.useId();
  const [open, setOpen] = useState(false);
  const selected = options.find((opt) => opt.value === value);
  const canOpen = !disabled && options.length > 1 && typeof onSelect === 'function';

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [value]);

  const handleSelect = (nextValue) => {
    if (nextValue !== value) {
      onSelect?.(nextValue);
    }
    setOpen(false);
  };

  const triggerLabel = selected?.label || formatLanguageDisplay(value);

  return (
    <div
      ref={containerRef}
      className={[
        'study-roadmap-context__dropdown',
        'playground-study-mode__language-dropdown',
        open && 'study-roadmap-context__dropdown--open',
        !canOpen && 'study-roadmap-context__dropdown--static',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={[
          'study-roadmap-context__chip',
          'study-roadmap-context__dropdown-trigger',
          'playground-study-mode__language-trigger',
        ].join(' ')}
        onClick={() => canOpen && setOpen((prev) => !prev)}
        disabled={!canOpen}
        aria-haspopup="listbox"
        aria-expanded={canOpen ? open : false}
        aria-controls={canOpen ? listId : undefined}
        title={triggerLabel}
      >
        <IconRenderer
          type="language"
          name={value}
          className="playground-study-mode__language-icon"
        />
        <span className="study-roadmap-context__chip-text">{triggerLabel}</span>
        {canOpen && (
          <FaChevronDown
            className="study-roadmap-context__dropdown-chevron"
            aria-hidden
          />
        )}
      </button>
      {open && canOpen && (
        <ul
          id={listId}
          className="study-roadmap-context__dropdown-menu"
          role="listbox"
          aria-label="Language"
        >
          {options.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={value === option.value}
                className={[
                  'study-roadmap-context__dropdown-option',
                  value === option.value &&
                    'study-roadmap-context__dropdown-option--selected',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleSelect(option.value)}
                title={option.label}
              >
                <IconRenderer
                  type="language"
                  name={option.value}
                  className="playground-study-mode__language-icon"
                />
                <span className="study-roadmap-context__dropdown-option-text">
                  {option.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const PlaygroundStudyMode = ({ userId }) => {
  const { availableLanguages, isLoading: languagesLoading } = useUserLanguages(userId);
  const languageOptions = useMemo(
    () => buildPlaygroundLanguageOptions(availableLanguages),
    [availableLanguages]
  );

  const readStoredLanguage = useCallback(() => {
    if (!userId) return 'javascript';
    const stored = localStorage.getItem(`${userId}${PLAYGROUND_LANGUAGE_STORAGE_SUFFIX}`);
    return normalizePlaygroundLanguage(stored || 'javascript');
  }, [userId]);

  const readStoredCode = useCallback(
    (languageKey) => {
      if (!userId) return '';
      return localStorage.getItem(playgroundCodeStorageKey(userId, languageKey)) || '';
    },
    [userId]
  );

  const [language, setLanguage] = useState(readStoredLanguage);
  const [code, setCode] = useState(() => readStoredCode(readStoredLanguage()));
  const [isExpanded, setIsExpanded] = useState(false);
  const [snippets, setSnippets] = useState(() => readPlaygroundSnippets(userId));
  const [activeSnippetId, setActiveSnippetId] = useState(() => readActiveSnippetId(userId));
  const [showSnippetIndex, setShowSnippetIndex] = useState(false);
  const [saveDialog, setSaveDialog] = useState({
    open: false,
    mode: 'new',
    snippetId: null,
    initialTitle: '',
  });
  const [saveFlash, setSaveFlash] = useState(false);
  const saveFlashTimerRef = useRef(null);
  const remoteUpsertTimersRef = useRef(new Map());
  const pendingRemoteSnippetsRef = useRef(new Map());

  const activeSnippet = findPlaygroundSnippet(snippets, activeSnippetId);
  const [toolbarTitle, setToolbarTitle] = useState(activeSnippet?.title || '');

  const applyEditorFromSnippets = useCallback(
    (nextSnippets, preferredActiveId) => {
      const storedActiveId =
        preferredActiveId !== undefined ? preferredActiveId : readActiveSnippetId(userId);
      const storedActiveSnippet = findPlaygroundSnippet(nextSnippets, storedActiveId);

      setSnippets(nextSnippets);
      setActiveSnippetId(storedActiveSnippet ? storedActiveId : null);
      if (!storedActiveSnippet && storedActiveId) {
        writeActiveSnippetId(userId, null);
      }

      if (storedActiveSnippet) {
        setLanguage(storedActiveSnippet.language);
        setCode(storedActiveSnippet.code);
        setToolbarTitle(storedActiveSnippet.title);
        return;
      }

      const nextLanguage = readStoredLanguage();
      setLanguage(nextLanguage);
      setCode(readStoredCode(nextLanguage));
      setToolbarTitle('');
    },
    [userId, readStoredLanguage, readStoredCode]
  );

  useEffect(() => {
    if (!userId || languagesLoading) return undefined;

    let cancelled = false;

    const hydrate = async () => {
      const localSnippets = readPlaygroundSnippets(userId);
      if (!cancelled) {
        applyEditorFromSnippets(localSnippets);
      }

      try {
        const remoteSnippets = await listPlaygroundSnippets(userId);
        if (cancelled) return;

        // Re-read local after the network round-trip so in-flight edits win merges.
        const latestLocal = readPlaygroundSnippets(userId);
        const merged = mergePlaygroundSnippets(latestLocal, remoteSnippets);
        const toUpload = snippetsNeedingRemoteUpsert(merged, remoteSnippets);
        if (toUpload.length) {
          await upsertPlaygroundSnippetsBatch(userId, toUpload);
        }
        if (cancelled) return;

        writePlaygroundSnippets(userId, merged);
        applyEditorFromSnippets(merged);
      } catch (error) {
        console.error('Failed to sync playground snippets:', error);
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [userId, languagesLoading, applyEditorFromSnippets]);

  useEffect(() => {
    if (activeSnippet) {
      setToolbarTitle(activeSnippet.title);
    } else {
      setToolbarTitle('');
    }
  }, [activeSnippet?.id, activeSnippet?.title]);

  const flushPendingRemoteUpserts = useCallback(() => {
    const timers = remoteUpsertTimersRef.current;
    const pending = pendingRemoteSnippetsRef.current;
    const user = userId;

    timers.forEach((timerId) => clearTimeout(timerId));
    timers.clear();

    if (!user || pending.size === 0) {
      pending.clear();
      return;
    }

    const payloads = Array.from(pending.values());
    pending.clear();
    payloads.forEach((snippet) => {
      upsertPlaygroundSnippet(user, snippet).catch((error) => {
        console.error('Failed to save playground snippet:', error);
      });
    });
  }, [userId]);

  useEffect(() => {
    return () => {
      if (saveFlashTimerRef.current) {
        clearTimeout(saveFlashTimerRef.current);
      }
      flushPendingRemoteUpserts();
    };
  }, [flushPendingRemoteUpserts]);

  const flashSaveSuccess = useCallback(() => {
    setSaveFlash(true);
    if (saveFlashTimerRef.current) {
      clearTimeout(saveFlashTimerRef.current);
    }
    saveFlashTimerRef.current = setTimeout(() => {
      setSaveFlash(false);
      saveFlashTimerRef.current = null;
    }, 400);
  }, []);

  const persistSnippetsLocal = useCallback(
    (nextSnippets) => {
      if (!userId) return;
      writePlaygroundSnippets(userId, nextSnippets);
      setSnippets(nextSnippets);
    },
    [userId]
  );

  const scheduleRemoteUpsert = useCallback(
    (snippet, { immediate = false } = {}) => {
      if (!userId || !snippet?.id) return;

      const timers = remoteUpsertTimersRef.current;
      const pending = pendingRemoteSnippetsRef.current;
      const existingTimer = timers.get(snippet.id);
      if (existingTimer) clearTimeout(existingTimer);

      pending.set(snippet.id, snippet);

      const run = () => {
        timers.delete(snippet.id);
        const payload = pending.get(snippet.id);
        pending.delete(snippet.id);
        if (!payload) return;
        upsertPlaygroundSnippet(userId, payload).catch((error) => {
          console.error('Failed to save playground snippet:', error);
        });
      };

      if (immediate) {
        run();
        return;
      }

      timers.set(
        snippet.id,
        setTimeout(run, PLAYGROUND_REMOTE_UPSERT_DEBOUNCE_MS)
      );
    },
    [userId]
  );

  const persistSnippets = useCallback(
    (nextSnippets, { remoteSnippetIds = [], immediate = false } = {}) => {
      persistSnippetsLocal(nextSnippets);
      if (!remoteSnippetIds.length) return;

      remoteSnippetIds.forEach((snippetId) => {
        const snippet = findPlaygroundSnippet(nextSnippets, snippetId);
        if (snippet) scheduleRemoteUpsert(snippet, { immediate });
      });
    },
    [persistSnippetsLocal, scheduleRemoteUpsert]
  );

  const persistCode = useCallback(
    (languageKey, nextCode) => {
      if (!userId) return;
      localStorage.setItem(playgroundCodeStorageKey(userId, languageKey), nextCode);
    },
    [userId]
  );

  const persistLanguage = useCallback(
    (activeLanguage) => {
      if (!userId) return;
      localStorage.setItem(
        `${userId}${PLAYGROUND_LANGUAGE_STORAGE_SUFFIX}`,
        normalizePlaygroundLanguage(activeLanguage)
      );
    },
    [userId]
  );

  const setActiveSnippet = useCallback(
    (snippetId) => {
      setActiveSnippetId(snippetId);
      writeActiveSnippetId(userId, snippetId);
    },
    [userId]
  );

  const clearActiveSnippet = useCallback(() => {
    setActiveSnippetId(null);
    writeActiveSnippetId(userId, null);
    setToolbarTitle('');
  }, [userId]);

  const syncCurrentSnippetState = useCallback(() => {
    if (!activeSnippetId) {
      persistCode(language, code);
      return null;
    }

    const nextSnippets = snippets.map((item) =>
      item.id === activeSnippetId
        ? updatePlaygroundSnippet(item, {
            code,
            language,
            title: normalizeSnippetTitle(toolbarTitle),
          })
        : item
    );
    persistSnippets(nextSnippets, {
      remoteSnippetIds: [activeSnippetId],
      immediate: true,
    });
    return findPlaygroundSnippet(nextSnippets, activeSnippetId);
  }, [
    activeSnippetId,
    language,
    code,
    toolbarTitle,
    snippets,
    persistSnippets,
    persistCode,
  ]);

  const switchLanguage = useCallback(
    (nextLanguage) => {
      const normalized = normalizePlaygroundLanguage(nextLanguage);
      if (normalized === language) return;

      syncCurrentSnippetState();
      clearActiveSnippet();

      const nextCode = readStoredCode(normalized);
      setLanguage(normalized);
      setCode(nextCode);
      persistLanguage(normalized);
    },
    [
      language,
      syncCurrentSnippetState,
      clearActiveSnippet,
      readStoredCode,
      persistLanguage,
    ]
  );

  const handleCodeChange = useCallback(
    (nextCode) => {
      setCode(nextCode);

      if (activeSnippetId) {
        const nextSnippets = snippets.map((item) =>
          item.id === activeSnippetId
            ? updatePlaygroundSnippet(item, { code: nextCode, language })
            : item
        );
        persistSnippets(nextSnippets, {
          remoteSnippetIds: [activeSnippetId],
          immediate: false,
        });
        return;
      }

      persistCode(language, nextCode);
    },
    [activeSnippetId, snippets, persistSnippets, language, persistCode]
  );

  const openSaveDialog = useCallback(
    (mode = 'new', snippetId = null) => {
      const titles = snippets.map((snippet) => snippet.title);
      const initialTitle =
        mode === 'rename' && snippetId
          ? findPlaygroundSnippet(snippets, snippetId)?.title || ''
          : toolbarTitle.trim() || suggestSnippetTitle(code, language, titles);

      setSaveDialog({
        open: true,
        mode,
        snippetId,
        initialTitle,
      });
    },
    [code, language, snippets, toolbarTitle]
  );

  const closeSaveDialog = useCallback(() => {
    setSaveDialog({
      open: false,
      mode: 'new',
      snippetId: null,
      initialTitle: '',
    });
  }, []);

  const handleConfirmSaveDialog = useCallback(
    (title) => {
      if (saveDialog.mode === 'rename' && saveDialog.snippetId) {
        const nextSnippets = snippets.map((item) =>
          item.id === saveDialog.snippetId
            ? updatePlaygroundSnippet(item, { title })
            : item
        );
        persistSnippets(nextSnippets, {
          remoteSnippetIds: [saveDialog.snippetId],
          immediate: true,
        });
        if (activeSnippetId === saveDialog.snippetId) {
          setToolbarTitle(title);
        }
        flashSaveSuccess();
        closeSaveDialog();
        return;
      }

      syncCurrentSnippetState();

      const nextSnippet = createPlaygroundSnippet({ title, language, code });
      const nextSnippets = [nextSnippet, ...snippets];
      persistSnippets(nextSnippets, {
        remoteSnippetIds: [nextSnippet.id],
        immediate: true,
      });
      setActiveSnippet(nextSnippet.id);
      setToolbarTitle(nextSnippet.title);
      setShowSnippetIndex(false);
      flashSaveSuccess();
      closeSaveDialog();
    },
    [
      saveDialog,
      snippets,
      persistSnippets,
      activeSnippetId,
      closeSaveDialog,
      syncCurrentSnippetState,
      language,
      code,
      setActiveSnippet,
      flashSaveSuccess,
    ]
  );

  const handleSaveClick = useCallback(() => {
    if (activeSnippetId) {
      syncCurrentSnippetState();
      flashSaveSuccess();
      return;
    }
    if (!code.trim()) return;
    openSaveDialog('new');
  }, [activeSnippetId, syncCurrentSnippetState, flashSaveSuccess, code, openSaveDialog]);

  const handleToolbarTitleChange = useCallback(
    (nextTitle) => {
      setToolbarTitle(nextTitle);
      if (!activeSnippetId) return;

      const normalized = normalizeSnippetTitle(nextTitle);
      const nextSnippets = snippets.map((item) =>
        item.id === activeSnippetId
          ? updatePlaygroundSnippet(item, { title: normalized })
          : item
      );
      persistSnippets(nextSnippets, {
        remoteSnippetIds: [activeSnippetId],
        immediate: false,
      });
    },
    [activeSnippetId, snippets, persistSnippets]
  );

  const handleSelectSnippet = useCallback(
    (snippetId) => {
      const snippet = findPlaygroundSnippet(snippets, snippetId);
      if (!snippet) return;

      if (activeSnippetId && activeSnippetId !== snippetId) {
        syncCurrentSnippetState();
      } else if (!activeSnippetId) {
        persistCode(language, code);
      }

      setLanguage(snippet.language);
      setCode(snippet.code);
      setToolbarTitle(snippet.title);
      persistLanguage(snippet.language);
      setActiveSnippet(snippet.id);
      setShowSnippetIndex(false);
    },
    [
      snippets,
      activeSnippetId,
      syncCurrentSnippetState,
      persistCode,
      language,
      code,
      persistLanguage,
      setActiveSnippet,
    ]
  );

  const handleDeleteSnippet = useCallback(
    (snippetId) => {
      const timers = remoteUpsertTimersRef.current;
      const pending = pendingRemoteSnippetsRef.current;
      const existingTimer = timers.get(snippetId);
      if (existingTimer) clearTimeout(existingTimer);
      timers.delete(snippetId);
      pending.delete(snippetId);

      const nextSnippets = snippets.filter((item) => item.id !== snippetId);
      persistSnippetsLocal(nextSnippets);
      deletePlaygroundSnippetRemote(userId, snippetId).catch((error) => {
        console.error('Failed to delete playground snippet:', error);
      });

      if (activeSnippetId === snippetId) {
        clearActiveSnippet();
        const nextCode = readStoredCode(language);
        setCode(nextCode);
      }
    },
    [
      snippets,
      persistSnippetsLocal,
      userId,
      activeSnippetId,
      clearActiveSnippet,
      readStoredCode,
      language,
    ]
  );

  const handleToggleSnippetIndex = useCallback(() => {
    setShowSnippetIndex((prev) => !prev);
  }, []);

  const handleNewPlayground = useCallback(() => {
    syncCurrentSnippetState();
    clearActiveSnippet();
    setCode('');
    persistCode(language, '');
    setShowSnippetIndex(false);
  }, [syncCurrentSnippetState, clearActiveSnippet, persistCode, language]);

  const handleRenameSnippet = useCallback(
    (snippetId) => {
      openSaveDialog('rename', snippetId);
    },
    [openSaveDialog]
  );

  const toolbarLanguageDot = getLangColors(formatLanguageDisplay(language)).bg;

  const handleToggleExpand = useCallback((event) => {
    event?.stopPropagation?.();
    setIsExpanded((prev) => !prev);
  }, []);

  const saveDisabled = !code.trim();
  const saveTitle = activeSnippetId
    ? 'Changes save automatically'
    : 'Save snippet';

  return (
    <div
      className={[
        'study-viewer',
        'study-viewer--playground',
        isExpanded ? 'expanded' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="study-viewer__layout">
        <div className="study-shell playground-study-mode__shell">
          <StudyNavbar
            snippetIcon={
              <IconRenderer
                type="language"
                name={language}
                className="study-shell__navbar-snippet-icon-svg"
              />
            }
            sectionName="Playground"
            controls={
              <PlaygroundLanguageDropdown
                value={language}
                options={languageOptions}
                onSelect={switchLanguage}
                disabled={languagesLoading}
              />
            }
          />

          <div className="study-shell__body playground-study-mode__editor-body">
            <StudyModeCodeEditor
              value={code}
              onChange={handleCodeChange}
              language={language}
              toolbarChrome
              showToolbarFilename
              toolbarFilename={toolbarTitle}
              toolbarFilenameEditable
              onToolbarFilenameChange={handleToolbarTitleChange}
              toolbarFilenamePlaceholder={PLAYGROUND_UNTITLED_SNIPPET_TITLE}
              toolbarLanguageLabel={formatLanguageDisplay(language)}
              toolbarLanguageDot={toolbarLanguageDot}
              expanded={isExpanded}
              onExpandClick={handleToggleExpand}
              onIndexClick={handleToggleSnippetIndex}
              indexOpen={showSnippetIndex}
              indexTitle="Open playground index"
              onSaveClick={handleSaveClick}
              saveSaved={saveFlash}
              saveDisabled={saveDisabled}
              saveTitle={saveTitle}
              alternateContent={
                showSnippetIndex ? (
                  <PlaygroundSnippetIndex
                    snippets={snippets}
                    activeSnippetId={activeSnippetId}
                    onSelectSnippet={handleSelectSnippet}
                    onRenameSnippet={handleRenameSnippet}
                    onDeleteSnippet={handleDeleteSnippet}
                    onNewPlayground={handleNewPlayground}
                  />
                ) : null
              }
              autoFocus={!showSnippetIndex}
            />
          </div>
        </div>
      </div>

      <PlaygroundSaveSnippetDialog
        open={saveDialog.open}
        mode={saveDialog.mode}
        initialTitle={saveDialog.initialTitle}
        language={language}
        codePreview={getSnippetCodePreview(code)}
        onCancel={closeSaveDialog}
        onConfirm={handleConfirmSaveDialog}
      />
    </div>
  );
};

export default PlaygroundStudyMode;
